import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { baseUrl } from "../services/config";

const SETTINGS_CACHE_KEY = "unitSettingsCache";
const SETTINGS_REQUEST_TIMEOUT_MS = 10000;

const UnitSettingsContext = createContext(null);

const readCachedSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_CACHE_KEY)) || null;
  } catch (error) {
    console.log("Unable to read cached unit settings:", error);
    return null;
  }
};

// Keep only non-sensitive values needed by the application shell. The API
// response currently contains credentials that must never be persisted here.
const getCacheableSettings = (settings) => ({
  unit_id: settings.unit_id,
  unit_name: settings.unit_name,
  head_office_url: settings.head_office_url,
  logo_file: settings.logo_file,
  start_logo_file: settings.start_logo_file,
  offline_student_print: settings.offline_student_print,
});

export const UnitSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(readCachedSettings);
  const [isLoading, setIsLoading] = useState(false);

  const refreshSettings = useCallback(async () => {
    if (!navigator.onLine) return null;

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), SETTINGS_REQUEST_TIMEOUT_MS)
      : null;

    setIsLoading(true);
    try {
      const authToken = localStorage.getItem("userToken");
      const response = await fetch(`${baseUrl}/get-settings`, {
        method: "GET",
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : undefined,
        signal: controller ? controller.signal : undefined,
      });

      if (!response.ok) {
        throw new Error(`Unable to load unit settings (${response.status}).`);
      }

      const data = await response.json();
      const nextSettings = data?.payload?.settings;
      if (!nextSettings) {
        throw new Error("The unit settings response is invalid.");
      }

      setSettings(nextSettings);
      localStorage.setItem(
        SETTINGS_CACHE_KEY,
        JSON.stringify(getCacheableSettings(nextSettings)),
      );
      return nextSettings;
    } catch (error) {
      console.log("Unable to refresh unit settings:", error);
      return null;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
    window.addEventListener("online", refreshSettings);

    return () => window.removeEventListener("online", refreshSettings);
  }, [refreshSettings]);

  const value = useMemo(
    () => ({ settings, isLoading, refreshSettings }),
    [settings, isLoading, refreshSettings],
  );

  return (
    <UnitSettingsContext.Provider value={value}>
      {children}
    </UnitSettingsContext.Provider>
  );
};

export const useUnitSettings = () => {
  const context = useContext(UnitSettingsContext);
  if (!context) {
    throw new Error("useUnitSettings must be used inside UnitSettingsProvider.");
  }
  return context;
};

export const isOfflineStudentPrintEnabled = (settings) =>
  settings?.offline_student_print === undefined ||
  settings?.offline_student_print === null
    ? true
    : Number(settings.offline_student_print) === 1;
